import React, { useState } from 'react';
import { IAuditoriaProvisaoEspelhoResultado, IAuditoriaProvisaoEspelhoItem } from '../interfaces/IAuditoriaProvisaoEspelho';

interface Props {
  resultado: IAuditoriaProvisaoEspelhoResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
    status === 'AVISO'  ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const ItemExpandivel: React.FC<{ item: IAuditoriaProvisaoEspelhoItem }> = ({ item }) => {
  const [expandido, setExpandido] = useState(false);

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandido(!expandido)}>
        <td>
          <span className='fw-bold'>{item.descricaoConta}</span>
          <br />
          <span className='text-muted fs-8'>{item.contaReferencial}</span>
        </td>
        <td className='fw-bold'>{formatCurrency(item.valorEspelho)}</td>
        <td>{formatDate(item.dataConstituicao)}</td>
        <td>{formatDate(item.dataReversao)}</td>
        <td>
          <span className={`fw-bold ${item.status === 'ERRO' ? 'text-danger' : 'text-warning'}`}>
            {item.diferencaTemporal} mês(es)
          </span>
          {item.diferencaPercentual > 0 && (
            <span className='text-muted fs-8 ms-1'>({item.diferencaPercentual.toFixed(2)}% diff)</span>
          )}
        </td>
        <td className='text-danger fw-bold'>{formatCurrency(item.passivoTributario)}</td>
        <td><StatusBadge status={item.status} /></td>
        <td className='text-end'>{expandido ? '▲' : '▼'}</td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={8} className='bg-light-secondary p-4'>
            <div className='mb-3'>
              <strong>Diagnóstico:</strong>
              <p className='text-muted mt-1 mb-2'>{item.mensagemDiagnostico}</p>
            </div>

            {item.status === 'ERRO' && (
              <div className='row mb-4'>
                <div className='col-3 text-center border-end'>
                  <div className='fs-4 fw-bolder text-danger'>{formatCurrency(item.tributoIrpjCsll)}</div>
                  <div className='text-muted fs-8'>IRPJ/CSLL (34%)</div>
                </div>
                <div className='col-3 text-center border-end'>
                  <div className='fs-4 fw-bolder text-danger'>{formatCurrency(item.multaArt44)}</div>
                  <div className='text-muted fs-8'>Multa Art. 44 (75%)</div>
                </div>
                <div className='col-3 text-center border-end'>
                  <div className='fs-4 fw-bolder text-warning'>{formatCurrency(item.jurosSelic)}</div>
                  <div className='text-muted fs-8'>Juros SELIC</div>
                </div>
                <div className='col-3 text-center'>
                  <div className='fs-4 fw-bolder text-danger'>{formatCurrency(item.passivoTributario)}</div>
                  <div className='text-muted fs-8'>Passivo Total</div>
                </div>
              </div>
            )}

            {item.lancamentos.length > 0 && (
              <div>
                <strong>Lançamentos do par espelho:</strong>
                <table className='table table-sm table-row-gray-200 mt-2'>
                  <thead>
                    <tr className='text-muted fw-bold fs-8'>
                      <th>Data</th>
                      <th>Tipo</th>
                      <th>Valor</th>
                      <th>Histórico</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.lancamentos.map((lc, i) => (
                      <tr key={i}>
                        <td>{formatDate(lc.dataLancamento)}</td>
                        <td>
                          <span className={`badge ${lc.tipoLancamento === 'C' ? 'badge-light-primary' : 'badge-light-warning'}`}>
                            {lc.tipoLancamento === 'C' ? 'C — Constituição' : 'D — Reversão'}
                          </span>
                        </td>
                        <td className='fw-bold'>{formatCurrency(lc.valor)}</td>
                        <td className='text-muted'>{lc.historico || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {item.status === 'ERRO' && (
              <div className='alert alert-danger mt-3 py-2'>
                <strong>CPC 25 — Regularização obrigatória:</strong> Provisão constituída e revertida com
                valor idêntico caracteriza Cookie Jar Accounting. Regularize via DCTF/EFD-Contribuições
                com recolhimento do passivo tributário estimado.
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaProvisaoEspelhoDetalhe: React.FC<Props> = ({ resultado }) => {
  const bannerCor =
    resultado.status === 'ERRO'   ? 'danger' :
    resultado.status === 'ALERTA' ? 'warning' :
    resultado.status === 'AVISO'  ? 'warning' :
                                    'success';

  return (
    <div className='card card-custom card-stretch'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>
            Provisões "Espelho" — Detecção de Cookie Jar Accounting
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            CPC 25 · Lei 6.404/76 Arts. 177 e 187 · Lei 9.430/96 Art. 44
          </span>
        </h3>
      </div>

      <div className='card-body pt-3'>
        {resultado.status !== 'CONCILIADO' && (
          <div className={`alert alert-${bannerCor} py-3 mb-5`}>
            <strong>{resultado.status}:</strong> {resultado.mensagemDirecionamento}
          </div>
        )}

        <div className='row mb-6'>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-danger'>{resultado.totalErros}</div>
            <div className='text-muted'>Erros (≥ R$ 10.000)</div>
            <div className='text-muted fs-8'>Cookie Jar Accounting</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAlertas}</div>
            <div className='text-muted'>Alertas (&lt; R$ 10.000)</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAvisos}</div>
            <div className='text-muted'>Avisos (&lt; 5% diff)</div>
          </div>
          <div className='col-3 text-center'>
            <div className='fs-1 fw-bolder text-danger'>{formatCurrency(resultado.passivoTributarioTotal)}</div>
            <div className='text-muted'>Passivo Tributário Total</div>
            <div className='text-muted fs-8'>IRPJ/CSLL + Multa 75% + SELIC</div>
          </div>
        </div>

        {resultado.itens.length === 0 ? (
          <div className='text-success fw-bold fs-5 text-center py-8'>
            ✓ Nenhum par espelho de provisão detectado. Situação regular.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Conta</th>
                  <th>Valor Espelho</th>
                  <th>Constituição</th>
                  <th>Reversão</th>
                  <th>Intervalo</th>
                  <th>Passivo</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemExpandivel key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <details className='mt-6'>
          <summary className='text-muted fw-bold cursor-pointer'>Base Legal</summary>
          <p className='text-muted mt-2 fs-7'>{resultado.fundamentacaoLegal}</p>
        </details>
      </div>
    </div>
  );
};

export default AuditoriaProvisaoEspelhoDetalhe;
